import { ExportOutlined } from '@ant-design/icons'
import { Handle, type Node, type NodeProps, Position } from '@xyflow/react'
import { Tooltip } from 'antd'
import { useContext, useState } from 'react'
import type { ValueNodeData } from './auditLayout'
import { AuditNavContext, type ReferenceLink } from './auditToggleContext'

export type AuditNodeType = Node<ValueNodeData, 'auditValue'>

const ROLE_LABEL: Record<ValueNodeData['role'], string> = {
  origin: 'Source value',
  master: 'Master',
  dest: 'Destination',
}

function borderColor(status: ValueNodeData['status']): string {
  if (status === 'error') return 'var(--theme-error)'
  if (status === 'warning') return 'var(--theme-warning)'
  return 'hsl(var(--border))'
}

function referenceLink(data: ValueNodeData): ReferenceLink | null {
  const { linkObject, role, linkGoid, linkSourceId, linkMappingId } = data
  if (role === 'master') {
    return linkGoid && linkObject
      ? { object: linkObject, tenantSide: 'master', goid: linkGoid }
      : null
  }
  // Origin/destination: link when we can filter (source id) and can resolve a
  // type (either the mapping id to fetch, or the field-inferred fallback).
  if (!linkSourceId || (!linkMappingId && !linkObject)) return null
  return {
    object: linkObject,
    tenantSide: role === 'origin' ? 'origin' : 'destination',
    sourceId: linkSourceId,
    mappingId: linkMappingId,
  }
}

export function AuditNode({ data }: NodeProps<AuditNodeType>) {
  const { name, sub, role, status, chosen, showTechnical } = data
  const accent = status !== 'ok'
  const openReference = useContext(AuditNavContext)
  const [hovered, setHovered] = useState(false)
  const link = referenceLink(data)

  const tooltip = (
    <div style={{ fontSize: 11 }}>
      <div style={{ opacity: 0.65 }}>
        {ROLE_LABEL[role]}
        {!chosen ? ' · candidate' : ''}
      </div>
      <div>{name}</div>
      {sub && sub !== name ? <div style={{ opacity: 0.65 }}>{sub}</div> : null}
    </div>
  )

  return (
    <Tooltip title={tooltip} mouseEnterDelay={0}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          position: 'relative',
          width: '100%',
          boxSizing: 'border-box',
          // re-enable events on the card so its hover tooltip fires despite the
          // node wrapper being pointer-events:none (canvas is locked)
          pointerEvents: 'auto',
          background: 'hsl(var(--card))',
          border: `${accent ? 1.5 : 1}px solid ${borderColor(status)}`,
          borderRadius: 'var(--radius)',
          padding: '7px 12px',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 2,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
        }}
      >
        {link && hovered ? (
          <button
            type='button'
            title='Open in reference data'
            onClick={(e) => {
              e.stopPropagation()
              openReference(link)
            }}
            style={{
              position: 'absolute',
              top: 3,
              right: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              padding: 0,
              border: 'none',
              borderRadius: 4,
              background: 'hsl(var(--muted))',
              color: 'hsl(var(--primary))',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            <ExportOutlined />
          </button>
        ) : null}

        {accent ? (
          <span
            style={{
              position: 'absolute',
              top: -7,
              right: -7,
              width: 16,
              height: 16,
              borderRadius: '50%',
              background: borderColor(status),
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
            }}
          >
            !
          </span>
        ) : null}

        {role !== 'origin' ? (
          <Handle type='target' position={Position.Left} />
        ) : null}

        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.3,
            color: 'hsl(var(--foreground))',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </div>
        {showTechnical && sub && sub !== name ? (
          <div
            style={{
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: 10.5,
              lineHeight: 1.3,
              color: 'hsl(var(--muted-foreground))',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {sub}
          </div>
        ) : null}

        {role !== 'dest' ? (
          <Handle type='source' position={Position.Right} />
        ) : null}
      </div>
    </Tooltip>
  )
}
